import {
  HttpErrorResponse,
  type HttpEvent,
  HttpHandler,
  type HttpInterceptor,
  type HttpHandlerFn,
  type HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { EnvironmentInjector, Injectable, inject, runInInjectionContext } from '@angular/core';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { TRACE_CONFIG } from './trace.config';
import { TraceService } from './trace.service';
import { TelemetryService } from './telemetry.service';

const SCHEME = /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//;

function hostOf(endpoint: string | null | undefined): string | null {
  if (!endpoint) {
    return null;
  }
  try {
    return new URL(SCHEME.test(endpoint) ? endpoint : `https://${endpoint}`).hostname.toLowerCase();
  } catch {
    return null;
  }
}

/**
 * Adds a W3C traceparent to requests to our own API hosts. Must run after the
 * URL interceptor so the resolved URL is used for the host check.
 */
export const traceInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const trace = inject(TraceService);
  const telemetry = inject(TelemetryService);
  if (!trace.isEnabled()) {
    return next(req);
  }

  const ownHosts = new Set(
    [
      inject<string | null>('apiRoot' as never, { optional: true }),
      inject<string | null>('baseAddress' as never, { optional: true }),
      inject<string | null>('xeroBaseAddress' as never, { optional: true }),
    ]
      .map(hostOf)
      .filter((host): host is string => host !== null),
  );
  const host = hostOf(req.url);
  if (!host || !ownHosts.has(host)) {
    return next(req);
  }

  const context = trace.createContext();
  const tracedReq = req.clone({
    headers: req.headers.set(TRACE_CONFIG.traceparentHeader, context.traceparent),
  });
  const startedAt = performance.now();
  const report = (status: number): void =>
    telemetry.recordHttpRequest(context.traceId, req.method, status, performance.now() - startedAt);

  return next(tracedReq).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        report(event.status);
      }
    }),
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        report(error.status);
        const backendReference = error.headers?.get(TRACE_CONFIG.responseReferenceHeader);
        // Same 8-char upper-case shape whichever side supplied the id (ADR §8).
        trace.recordErrorReference(
          error,
          trace.toReference(backendReference?.trim() || context.traceId),
        );
      }
      return throwError(() => error);
    }),
  );
};

/**
 * Class-based wrapper registered through HTTP_INTERCEPTORS. Unlike a functional
 * interceptor passed to one provideHttpClient(), DI interceptors are also
 * picked up by the extra HttpClient that OdinCoreModule provides in lazy
 * modules, so every client carries the traceparent.
 */
@Injectable()
export class TraceHttpInterceptor implements HttpInterceptor {
  private readonly injector = inject(EnvironmentInjector);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return runInInjectionContext(this.injector, () =>
      traceInterceptor(req, (request) => next.handle(request)),
    );
  }
}
